import React, { useEffect, useState } from "react";
import { X, Bug, Loader2, ExternalLink, CheckCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";

interface VulnerabilityData {
    id: number;
    cve_id: string;
    published_at: string;
    last_modified: string;
    cve_description: string;
    vuln_status: string;
    cvss_version: string;
    cvss_score: number;
    cvss_severity: string;
    cvss_vector: string;
    exploitability_score: number;
    impact_score: number;
    attack_vector: string;
    attack_complexity: string;
    privileges_required: string;
    user_interaction: string;
    scope: string;
    confidentiality_impact: string;
    integrity_impact: string;
    availability_impact: string;
    cpe_vendor: string;
    cpe_product: string;
    cpe_uri: string;
    severity: string;
    match_type: string;
}

interface ApplicationVulnerabilitiesSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    applicationId: number | null;
    appName: string;
}

const ApplicationVulnerabilitiesSidebar: React.FC<ApplicationVulnerabilitiesSidebarProps> = ({
    isOpen,
    onClose,
    applicationId,
    appName
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [vulnerabilities, setVulnerabilities] = useState<VulnerabilityData[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [expandedCves, setExpandedCves] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isOpen]);

    useEffect(() => {
        async function fetchVulnerabilities() {
            if (!applicationId) return;
            setLoading(true);
            try {
                const response = await baseapi.get(`/applications/${applicationId}/vulnerabilities`);
                if (response.data?.status || response.status === 200) {
                    setVulnerabilities(response.data?.data || []);
                    setTotal(response.data?.total || 0);
                }
            } catch (error: any) {
                showError(error.message || "Failed to load vulnerabilities for this application");
                setVulnerabilities([]);
                setTotal(0);
            } finally {
                setLoading(false);
            }
        }

        if (isOpen && applicationId) {
            fetchVulnerabilities();
        } else {
            setVulnerabilities([]);
            setTotal(0);
            setExpandedCves({});
        }
    }, [isOpen, applicationId]);

    const toggleCveExpansion = (cveId: string) => {
        setExpandedCves((prev) => ({ ...prev, [cveId]: !prev[cveId] }));
    };

    const getSeverityStyle = (severity: string | null, score: number | null) => {
        if (!severity && !score) return "bg-gray-800 text-gray-200 border-gray-700";
        const s = (severity || "").toLowerCase();
        const val = score || 0;
        if (s.includes("critical") || val >= 9.0) return "bg-red-500/10 text-red-400 border-red-500/30";
        if (s.includes("high") || val >= 7.0) return "bg-orange-500/10 text-orange-400 border-orange-500/30";
        if (s.includes("medium") || val >= 4.0) return "bg-yellow-500/10 text-yellow-400 border-yellow-500/30";
        return "bg-green-500/10 text-green-400 border-green-500/30";
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return "-";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric"
            });
        } catch {
            return "-";
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
                    />

                    {/* Sidebar container */}
                    <motion.div
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ type: "spring", damping: 25, stiffness: 200 }}
                        className="fixed top-0 right-0 h-full w-full sm:w-[500px] md:w-[600px] lg:w-[680px] bg-gray-950 shadow-2xl z-[110] flex flex-col border-l border-gray-800"
                    >
                        {/* Header */}
                        <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-4 flex items-center justify-between border-b border-gray-700 shadow-lg shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-gradient-to-br from-red-500 to-rose-600 rounded-lg shadow-lg border border-white/10">
                                    <Bug className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-sm font-bold text-white tracking-tight leading-tight">
                                        Vulnerability Report
                                    </h2>
                                    <div className="flex items-center gap-1.5 mt-0.5">
                                        <span className="text-blue-400 text-[11px] font-semibold">{appName}</span>
                                        <span className="text-gray-400 text-[10px]">•</span>
                                        <span className="text-gray-200 text-[10px] font-bold uppercase tracking-wider">
                                            {total} {total === 1 ? "CVE" : "CVEs"} Identified
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-1.5 hover:bg-gray-800 rounded-lg transition-all duration-200 text-gray-200 hover:text-white border border-transparent hover:border-gray-700"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scroll bg-gray-900/30">
                            {loading ? (
                                <div className="flex flex-col items-center justify-center py-32 space-y-3">
                                    <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                                    <span className="text-[11px] font-bold uppercase tracking-widest text-gray-400 italic animate-pulse">
                                        Fetching Vulnerabilities...
                                    </span>
                                </div>
                            ) : vulnerabilities.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
                                    <div className="p-3 bg-green-500/10 rounded-full border border-green-500/20 mb-3">
                                        <CheckCircle className="w-10 h-10 text-green-400" />
                                    </div>
                                    <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-1">
                                        No Vulnerabilities Found
                                    </h3>
                                    <p className="text-xs text-gray-400 max-w-sm">
                                        No CVE matches were found for {appName}. The software is currently secure according to feed definitions.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {vulnerabilities.map((vuln) => {
                                        const isExpanded = expandedCves[vuln.cve_id];
                                        return (
                                            <div
                                                key={vuln.id}
                                                className="bg-gray-900 border border-gray-800/80 hover:border-gray-700/60 p-4 rounded-xl flex flex-col space-y-3 shadow-md transition-all duration-150"
                                            >
                                                {/* Header Line */}
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <a
                                                            href={`https://nvd.nist.gov/vuln/detail/${vuln.cve_id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="font-mono text-[11px] font-black text-red-400 bg-red-500/10 hover:bg-red-500/20 px-2.5 py-0.5 rounded border border-red-500/20 flex items-center gap-1 transition-colors"
                                                        >
                                                            {vuln.cve_id}
                                                            <ExternalLink size={10} />
                                                        </a>
                                                        <span
                                                            className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest border ${getSeverityStyle(
                                                                vuln.cvss_severity || vuln.severity,
                                                                vuln.cvss_score
                                                            )}`}
                                                        >
                                                            {vuln.cvss_severity || vuln.severity || "UNKNOWN"}
                                                        </span>
                                                    </div>
                                                    <div className="text-right shrink-0">
                                                        <span className="text-[9px] font-bold text-gray-400 uppercase block leading-none">
                                                            CVSS Score
                                                        </span>
                                                        <span className="text-[15px] font-black text-orange-400 font-mono">
                                                            {vuln.cvss_score?.toFixed(1) || "-"}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Description */}
                                                <div>
                                                    <p
                                                        onClick={() => toggleCveExpansion(vuln.cve_id)}
                                                        className={`text-gray-300 text-[11px] leading-relaxed cursor-pointer hover:text-white transition-colors ${
                                                            isExpanded ? "" : "line-clamp-3"
                                                        }`}
                                                    >
                                                        {vuln.cve_description || "No description available."}
                                                    </p>
                                                    {vuln.cve_description && vuln.cve_description.length > 180 && (
                                                        <button
                                                            onClick={() => toggleCveExpansion(vuln.cve_id)}
                                                            className="text-orange-500 hover:text-orange-400 text-[9px] font-black uppercase tracking-wider mt-1 focus:outline-none"
                                                        >
                                                            {isExpanded ? "Show Less" : "Read Full Description"}
                                                        </button>
                                                    )}
                                                </div>

                                                {/* Grid details */}
                                                <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 pt-2.5 border-t border-gray-800/80 text-[10px]">
                                                    <div className="flex flex-col">
                                                        <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">
                                                            Attack Vector
                                                        </span>
                                                        <span className="text-gray-200 font-semibold">
                                                            {vuln.attack_vector || "-"}
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">
                                                            Complexity
                                                        </span>
                                                        <span className="text-gray-200 font-semibold">
                                                            {vuln.attack_complexity || "-"}
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">
                                                            User Interaction
                                                        </span>
                                                        <span className="text-gray-200 font-semibold">
                                                            {vuln.user_interaction || "-"}
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">
                                                            Match Type
                                                        </span>
                                                        <span className="text-gray-200 font-semibold">
                                                            {vuln.match_type || "-"}
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-col col-span-2">
                                                        <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">
                                                            CPE URI
                                                        </span>
                                                        <span className="text-gray-200 font-mono text-[9px] bg-black/40 border border-gray-800 px-1.5 py-0.5 rounded break-all">
                                                            {vuln.cpe_uri || "-"}
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">
                                                            Published At
                                                        </span>
                                                        <span className="text-gray-200 font-mono">
                                                            {formatDate(vuln.published_at)}
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">
                                                            Last Modified
                                                        </span>
                                                        <span className="text-gray-200 font-mono">
                                                            {formatDate(vuln.last_modified)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-4 bg-gray-900 border-t border-gray-800 flex items-center justify-end shrink-0">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg font-bold text-[10px] uppercase tracking-widest hover:bg-gray-700 hover:text-white transition-all border border-gray-700"
                            >
                                Close Report
                            </button>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default ApplicationVulnerabilitiesSidebar;
